import React, { useMemo, useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { push } from 'connected-react-router'
import { Helmet } from 'react-helmet'

import { Link } from 'react-router-dom'
import camelcaseKeys from 'camelcase-keys'

import { SITE_TYPES } from '../../../utils/constants'
import reserveApi from '../../../utils/axios'
import { Button, ButtonLink } from '../../utils/Button'
import { HeaderLarge as Header } from '../../utils/Header'
import { Join, JoinAnd } from '../../utils/Join'
import Card from '../../utils/Card'
import { fetchFacility } from '../../../actions/facilitiesActions'
import { formSetFacility2, formStepInc } from '../../../actions/requestFormActions'

export const FacilityGroupShow = ({ match }) => {
  const dispatch = useDispatch()
  const id = useMemo(() => match.params.id, [match])

  const [facilityGroup, setFacilityGroup] = useState({ sitesDetails: {} })

  useEffect(() => {
    reserveApi.get(`/marketing/facility_groups/${id}`).then(response => {
      setFacilityGroup(camelcaseKeys(response.data, { deep: true }))
    })
  }, [id])

  const newRequest = facilityId => {
    dispatch(fetchFacility(facilityId, false)).then(() => {
      dispatch(formSetFacility2())
      dispatch(formStepInc())
      dispatch(push(`/new`))
    })
  }

  const hookups = useMemo(
    () =>
      [
        facilityGroup.sitesDetails.water && 'Water',
        facilityGroup.sitesDetails.electric && 'Electric',
        facilityGroup.sitesDetails.sewer && 'Sewer'
      ].filter(hu => hu !== false),
    [facilityGroup]
  )

  const dryCamping = useMemo(() => hookups.length === 0, [hookups])

  return (
    <div>
      <Header header={facilityGroup.name} subHeader={facilityGroup.marketingName} />
      <div className="mt-6 md:flex space-x-0 md:space-x-4">
        <div className="w-full md:w-2/3 lg:w-1/2">
          <div className="prose-xl">
            <p>
              At {facilityGroup.facilityCount} separate locations, {facilityGroup.name} has {facilityGroup.siteCount}{' '}
              reservable campsites. Those items include{' '}
              <JoinAnd
                items={Object.keys(facilityGroup?.sitesDetails?.types || {}).map(k => (
                  <React.Fragment key={k}>
                    {facilityGroup.sitesDetails.types[k]} {k}
                  </React.Fragment>
                ))}
              />
              .
            </p>

            {Object.keys(facilityGroup?.sitesDetails?.types || {}).includes('rv') && (
              <p>
                The {facilityGroup.sitesDetails.types['rv']} rv campsites have a max length of{' '}
                {facilityGroup.sitesDetails.maxLength} feet. {dryCamping && 'There are no hookups at any campsites.'}
                {!dryCamping && 'Some of these campsites offer '}
                {!dryCamping && <JoinAnd items={hookups} />}
              </p>
            )}
          </div>
        </div>
        <div className="w-full md:w-1/3 lg:w-1/2 mt-12 md:mt-0">
          <Card color="gray" className="prose-xl">
            <p className="font-semibold">Campsites all sold out? Reservations full? No availability? </p>
            <p>Create a new request with our service and we will alert you if a campsite becomes available</p>
            <p>Try it for free</p>
            <ButtonLink to={'/new'} color="green" className="flex-none">
              Create Notification Request
            </ButtonLink>
          </Card>
        </div>
      </div>
      <ul className="mt-12">
        {facilityGroup.facilities &&
          facilityGroup.facilities.map(facility => (
            <li
              key={facility.id}
              className="py-4 border-t-2 border-gray-300 flex flex-wrap justify-between items-start space-x-0 md:space-x-2 space-y-2"
            >
              <div className="flex-shrink-0 max-w-full">
                <div className="text-2xl font-semibold">
                  <Link to={`/f/${facility.slug}`}>{facility.name}</Link>
                </div>
                <p className="text-lg text-gray-500">
                  Tracking {facility.sitesCount} Sites (
                  <Join
                    items={Object.keys(facility?.sitesDetails?.types || []).map(type => (
                      <React.Fragment key={type}>
                        {SITE_TYPES[type] || type}: {facility.sitesDetails.types[type]}
                      </React.Fragment>
                    ))}
                  />
                  )
                </p>
              </div>
              <div className="flex space-x-4">
                <ButtonLink to={`/f/${facility.slug}`} color="green" className="flex-none">
                  Explore sites and more!
                </ButtonLink>

                <Button onClick={() => newRequest(facility.id)} color="orange" className="flex-none">
                  Create Alert
                </Button>
              </div>
            </li>
          ))}
      </ul>
      <Helmet>
        <title>{`${facilityGroup.name} - wanderingLabs.com - Campsite reservation and cancellation notifications`}</title>
        <meta name="description" content={facilityGroup.description} />
        <link rel="canonical" href={`https://wanderinglabs.com/explore/${facilityGroup.slug}`} />
        <meta
          property="og:title"
          content={`${facilityGroup.name} - wanderingLabs.com - Campsite reservation and cancellation notification`}
        />
        <meta property="og:description" content={facilityGroup.description} />
        <meta property="og:url" content={`https://wanderinglabs.com/f/${facilityGroup.slug}`} />
      </Helmet>
    </div>
  )
}

export default FacilityGroupShow
